import { BasketSummarySubjectType } from '@api/api/basket-summary';
import {
  KgInsightFilterV2Request,
  KgInsightFilterV2Response,
  KgInsightMention,
  KgInsightMentionsRequest,
  KgInsightMentionsResponse,
  kgInsights,
} from '@api/api/kg-insights';
import { wretchRequest } from '@terminal/utils/api-fetch/apiFetch';
import { startOfDay, subDays } from 'date-fns';
import { filterAndDeduplicateBasketMentions } from '../utils/msBasketSentiment';

export const QUERY_KEY_THEME_MENTIONS = 'QUERY_KEY_THEME_MENTIONS';

export const MENTIONS_WINDOW_DAYS = 30;

const PAGE_SIZE = 500;

export const fetchSubjectMentions = async (
  id: string,
  subjectType: BasketSummarySubjectType,
  signal?: AbortSignal
): Promise<KgInsightMentionsResponse> => {
  const to = new Date();
  const from = startOfDay(subDays(to, MENTIONS_WINDOW_DAYS));
  const mentions: KgInsightMention[] = [];

  for (let page = 1; ; page++) {
    const body: KgInsightMentionsRequest = {
      subject_type: subjectType,
      subject_id: id,
      from: from.toISOString(),
      to: to.toISOString(),
      page,
      page_size: PAGE_SIZE,
    };

    const response = await wretchRequest<KgInsightMentionsResponse>(
      kgInsights.postKgInsightMentions.path,
      {
        method: 'post',
        body,
        schema: kgInsights.postKgInsightMentions.schema,
        signal,
      }
    );

    mentions.push(...response.mentions);

    if (response.mentions.length < PAGE_SIZE) {
      break;
    }
  }

  return { mentions };
};

export const fetchThemeMentions = (
  themeId: string,
  signal?: AbortSignal
): Promise<KgInsightMentionsResponse> =>
  fetchSubjectMentions(themeId, 'theme', signal);

export const fetchBasketMentions = async (
  themeId: string,
  companyIds: string[],
  signal?: AbortSignal
): Promise<KgInsightMentionsResponse> => {
  const to = new Date();
  const from = startOfDay(subDays(to, MENTIONS_WINDOW_DAYS));
  const mentions: KgInsightMention[] = [];
  const allowedCompanies = new Set(companyIds);
  const basketThemes = new Set([themeId]);

  for (let page = 1; ; page++) {
    const body: KgInsightFilterV2Request = {
      subjects: [{ type: 'theme', id: themeId }],
      subject_scope: 'mentions',
      from: from.toISOString(),
      to: to.toISOString(),
      page,
      page_size: PAGE_SIZE,
    };
    const response = await wretchRequest<KgInsightFilterV2Response>(
      kgInsights.postKgInsightFilterV2.path,
      {
        method: 'post',
        body,
        schema: kgInsights.postKgInsightFilterV2.schema,
        signal,
      }
    );

    mentions.push(
      ...filterAndDeduplicateBasketMentions(
        response,
        allowedCompanies,
        basketThemes
      )
    );
    if (response.length < PAGE_SIZE) {
      break;
    }
  }

  return { mentions };
};
